import { Component, Input, signal, inject } from '@angular/core';
import { CommonModule, TitleCasePipe } from '@angular/common';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatCardModule } from '@angular/material/card';
import { MatChipsModule } from '@angular/material/chips';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ColimindDebugService } from '../../../services/colimind/colimind-debug.service';
import { ChartWidgetComponent } from '../chart-widget/colimind-chart-widget.component';
import { DataTableComponent } from '../data-table/colimind-data-table.component';
import { isTimeCol, isNumericType, formatMessageTime } from '../colimind-utils';
import { formatAnswerHtml, formatMarkdownHtml, FreshTextMark, stripAttachments } from '../colimind-answer-format';
import { ALERT_COLS, formatCellValue } from '../colimind-table-format';
import type { ChatResponse, WidgetType, QueryResultColumn } from '../colimind-types';

const ALERT_VALUES = ['ERROR', 'WARNING', 'FAILED', 'CRITICAL'];
const GAUGE_KEYS = ['target', 'goal', 'threshold', 'limit', 'benchmark'];
const SKIP_SCHEMA = ['workspace', 'default', 'catalog', 'hive_metastore'];

/**
 * Shared fallbacks for the "no data" case. A fresh `[]` per call would give every change
 * detection cycle a new identity, which is exactly what the memos below compare on -- the
 * caches would never hit for an empty response.
 */
const NO_ROWS: string[][] = [];
const NO_COLUMNS: QueryResultColumn[] = [];
const NO_SOURCES: ChatResponse['sources'] = [];

/**
 * Remembers one derived value per source. Several template bindings have to return the same
 * instance while their input does not change: a new array or object on every check makes
 * `@for` and MatTable rebuild mid-cycle, which Angular reports as NG0100.
 */
class Memo<S, T> {
  private cached?: { source: S; value: T };

  get(source: S, compute: () => T): T {
    if (!this.cached || this.cached.source !== source) {
      this.cached = { source, value: compute() };
    }
    return this.cached.value;
  }
}

@Component({
  selector: 'colimo-response-card',
  standalone: true,
  imports: [
    CommonModule,
    TitleCasePipe,
    MatExpansionModule,
    MatTooltipModule,
    MatIconModule,
    MatCardModule,
    MatChipsModule,
    TranslateModule,
    ChartWidgetComponent,
    DataTableComponent,
  ],
  templateUrl: './colimind-response-card.component.html',
  styleUrls: ['./colimind-response-card.component.scss'],
})
export class ResponseCardComponent {
  @Input() text = '';
  @Input() response?: ChatResponse;
  @Input() timestamp?: any;
  /** While streaming, new text is highlighted for a moment. */
  @Input() streaming = false;

  copied = signal(false);
  showRawData = signal(false);
  readonly debug = inject(ColimindDebugService);
  private sanitizer = inject(DomSanitizer);
  private translate = inject(TranslateService);

  private renderedSource?: string;
  private renderedText?: string;
  private renderedStreaming = false;
  private renderedHtml: SafeHtml = '';
  private freshMarks: FreshTextMark[] = [];

  /** Rendered once per text, not on every change detection run. */
  get safeHtml(): SafeHtml {
    if (this.text === this.renderedSource && this.streaming === this.renderedStreaming) return this.renderedHtml;
    this.renderedSource = this.text;
    const text = stripAttachments(this.text ?? '');

    const now = Date.now();
    const previous = this.renderedText ?? '';
    if (this.streaming && previous && text.startsWith(previous)) {
      this.freshMarks.push({ offset: previous.length, at: now });
    } else if (this.streaming && !previous) {
      this.freshMarks = [{ offset: 0, at: now }];
    } else {
      this.freshMarks = [];
    }

    this.renderedText = text;
    this.renderedStreaming = this.streaming;
    // Tables and lists are only complete once the answer is, so markdown waits for the end
    const html = this.streaming ? formatAnswerHtml(text, this.freshMarks, now) : formatMarkdownHtml(text);
    this.renderedHtml = this.sanitizer.bypassSecurityTrustHtml(html);
    return this.renderedHtml;
  }

  get widgetType(): WidgetType {
    const qr = this.response?.query_result;
    if (!qr || qr.row_count === 0) return 'text';
    if (qr.row_count > 500) return 'table';

    const cols = qr.columns;
    const rows = qr.data_array as string[][];
    const n = rows.length;

    const timeCols = cols.filter(isTimeCol);
    const numericCols = cols.filter(isNumericType);
    const catCols = cols.filter(c => c.type_name === 'STRING' && !isTimeCol(c));

    const alertColIdx = cols.findIndex(c => ALERT_COLS.includes(c.name.toLowerCase()));
    if (alertColIdx >= 0) return 'alert';

    const hasAlertValue = cols.some(
      (c, i) =>
        ALERT_COLS.includes(c.name.toLowerCase()) &&
        rows.some(r => ALERT_VALUES.includes(String(r[i]).toUpperCase())),
    );
    if (hasAlertValue) return 'alert';

    if (n === 1 && cols.some(c => GAUGE_KEYS.some(k => c.name.toLowerCase().includes(k))))
      return 'gauge';

    if (n === 1 && cols.length === 1) return 'code';
    if (n === 1) return 'keyvalue';

    if (timeCols.length > 0 && numericCols.length === 1) return 'line_chart';

    if (timeCols.length > 0 && numericCols.length >= 2 && numericCols.length <= 3 && cols.length <= 6)
      return 'multi_line_chart';

    if (catCols.length === 1 && numericCols.length === 1 && n <= 20) return 'bar_chart';

    if (catCols.length === 1 && numericCols.length === 1 && n > 20) return 'box_plot';

    if (catCols.length >= 2 && numericCols.length === 1) return 'stacked_bar_chart';

    if (numericCols.length >= 2 && timeCols.length === 0) return 'scatter_plot';

    if (numericCols.length === 1 && catCols.length === 0 && n > 30) return 'histogram';

    return 'table';
  }

  get rows(): string[][] {
    return (this.response?.query_result?.data_array ?? NO_ROWS) as string[][];
  }

  get columns(): QueryResultColumn[] {
    return this.response?.query_result?.columns ?? NO_COLUMNS;
  }

  private keyValueMemo = new Memo<QueryResultColumn[], { label: string; value: string }[]>();

  /**
   * Built once per response rather than on every check. As a getter it produced brand new
   * objects each cycle, so `@for` with identity tracking tore the whole list down and rebuilt
   * it, which surfaced as NG0100 on the label binding.
   */
  get keyValuePairs(): { label: string; value: string }[] {
    const cols = this.columns;
    return this.keyValueMemo.get(cols, () => {
      const first = this.rows[0];
      if (!first) return [];
      return cols.map((col, i) => ({
        label: col.name.replace(/_/g, ' '),
        value: this.formatValue(col.name, first[i]),
      }));
    });
  }

  private sourceTablesMemo = new Memo<ChatResponse['sources'], string[]>();

  /** Cached for the same reason as keyValuePairs: a fresh array each check rebuilds the chips. */
  get sourceTables(): string[] {
    const sources = this.response?.sources ?? NO_SOURCES;
    return this.sourceTablesMemo.get(sources, () => {
      const names = sources.map(s => s.table).filter(t => t && !SKIP_SCHEMA.includes(t));
      return [...new Set(names)].slice(0, 2);
    });
  }

  private formattedDateMemo = new Memo<unknown, string>();

  /** Same format as below a question; empty without a timestamp, never the current time. */
  get formattedDate(): string {
    return this.formattedDateMemo.get(this.timestamp, () => formatMessageTime(this.timestamp));
  }

  formatValue(colName: string, value: unknown): string {
    return formatCellValue(this.columns.find(c => c.name === colName), value);
  }

  private alertSeverity(row: string[]): 'critical' | 'warning' | 'ok' | 'unknown' {
    const SEVERITY_PRIORITY = ['severity', 'status', 'alert_type', 'level'];
    const idx = SEVERITY_PRIORITY
      .map(name => this.columns.findIndex(c => c.name.toLowerCase() === name))
      .find(i => i >= 0) ?? -1;
    if (idx < 0) return 'unknown';
    const val = String(row[idx]).toUpperCase();
    if (val.includes('CRITICAL') || val.includes('RED') || val.includes('ERROR')) return 'critical';
    if (val.includes('WARN') || val.includes('YELLOW') || val.includes('ORANGE')) return 'warning';
    if (val.includes('OK') || val.includes('GREEN') || val.includes('INFO') || val.includes('GOOD')) return 'ok';
    return 'unknown';
  }

  alertColor(row: string[]): 'warn' | 'accent' | 'primary' | '' {
    const s = this.alertSeverity(row);
    return s === 'critical' ? 'warn' : s === 'warning' ? 'accent' : s === 'ok' ? 'primary' : '';
  }

  alertLabel(row: string[]): string {
    const LABEL_COLS = ['kpi_name', 'color_name', 'part', 'line', 'message', 'description', 'name'];
    const parts = LABEL_COLS
      .map(name => this.columns.findIndex(c => c.name.toLowerCase() === name))
      .filter(i => i >= 0).map(i => String(row[i] ?? '').trim()).filter(v => v.length > 0).slice(0, 3);
    if (parts.length > 0) return parts.join(' · ');
    const skip = new Set(['alert_id', 'id', 'uuid', 'batch_id', 'created_at', 'created_at_utc', 'updated_at', 'tenant_id']);
    const fallback = this.columns.find(c => !skip.has(c.name.toLowerCase()) && c.type_name === 'STRING');
    return fallback ? String(row[this.columns.indexOf(fallback)] ?? '') : (row[0] ?? '');
  }

  toggleRawData(): void { this.showRawData.update(v => !v); }

  async copy(): Promise<void> {
    const watermark = this.translate.instant('colimind.watermark');
    await navigator.clipboard.writeText(`${stripAttachments(this.text).trim()}\n\n${watermark}`);
    this.copied.set(true);
    setTimeout(() => this.copied.set(false), 2000);
  }
}
